import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useDebouncedCallback } from "use-debounce";
import { useQueryClient } from "@tanstack/react-query";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import Spinner from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import UnsavedChangesWarningDialog from "@/components/layouts/UnsavedChangesWarningDialog";
import { DetailedViewOptions } from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import { Send } from "@dreamhub/designsystem/icons";
import { ProposalStatusBadge } from "../ProposalStatusBadge";
import {
  proposalPreviewQuery,
  SaveVariables,
  useProposalPreview,
  useTemplateFiles
} from "../hooks";
import { ProposalPages } from "../document/ProposalPages";
import { HighlightedSlotContext } from "../document/highlightedSlot";
import {
  includedOf,
  slotsInReadingOrder,
  slotsShown,
  withValues
} from "../document/withValues";
import { useProposalsApi } from "../api";
import {
  Proposal,
  ProposalDocument,
  ProposalRecipients,
  ProposalSection,
  ProposalStatus,
  ProposalVersion,
  StoredDraft
} from "../types";
import { SectionRail } from "./SectionRail";
import { SlotField } from "./SlotField";
import { slotFlag } from "./slotFlags";
import { PageOverlay } from "./PdfDocument";
import { SlotOverlay } from "./SlotOverlay";
import {
  holdsSend,
  isBlank,
  needsInput,
  SlotValues,
  slotValue
} from "./blanks";
import { downloadFile, proposalFileName } from "./download";
import { useLeaveGuard } from "./useLeaveGuard";
import {
  CARD,
  DeletedNotice,
  DOCUMENT_PANE,
  DocumentPane,
  DownloadButton,
  EditorHeader,
  OpenError,
  PAGE_HEIGHT_CLASS,
  PANE_ROW,
  ProposalActionsMenu
} from "./documentLayout";
import { dealPath } from "./paths";
import {
  regenerateOption,
  renameOption,
  reviseOption,
  withdrawOption
} from "./menuOptions";
import { useProposalRename } from "./useProposalRename";
import { inDocumentOrder } from "./slotOrder";
import {
  isRefusedWhileSending,
  saveErrorMessageKey
} from "../saveErrorMessage";
import {
  blankFieldsMessage,
  isSenderNotMatched,
  isSendStillRunning,
  sendErrorMessageKey
} from "../sendErrorMessage";
import { generateErrorMessageKey } from "../generateErrorMessage";
import { releaseDocumentUrl } from "../generatedDocument";
import {
  isDeletable,
  isRegenerable,
  isSavable,
  isRevisable,
  isSendable,
  isSendInProgress,
  isWithdrawable,
  withdrawsOnRevise
} from "../draftActions";
import {
  DeleteProposalDialog,
  RegenerateProposalDialog,
  ReviseProposalDialog,
  SendAccessRequiredDialog,
  WithdrawProposalDialog
} from "../ProposalActionDialogs";
import {
  reviseErrorMessageKey,
  withdrawErrorMessageKey
} from "../reviseErrorMessage";
import { ProposalTitle } from "./ProposalTitle";
import {
  ReadOnlyProposalActions,
  ReadOnlyProposalView
} from "./ReadOnlyProposalView";
import { RecipientsCard } from "../recipients/RecipientsCard";
import { RecipientsModal } from "../recipients/RecipientsModal";
import { sameRecipients, signerCount } from "../recipients/recipients";
import { SendConnection, useSendConnection } from "../sendConnection";

const PANDADOC_SETTINGS_PATH = "/settings/integrations/pandadoc";

// The rail's last item, after the template's own sections.
const RECIPIENTS_SECTION_ID = "__recipients";
// How long the rep stops typing before the document itself is recomposed.
const RECOMPOSE_PAUSE_MS = 1000;
// The slot contractsapi fills with the proposal's own name.
const PROPOSAL_NAME_SLOT = "proposal.name";

const useRailSections = (sections: ProposalSection[]) => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      ...sections,
      {
        id: RECIPIENTS_SECTION_ID,
        title: t("contracts.proposals.editor.recipients.title")
      }
    ],
    [sections, t]
  );
};

interface SaveBarProps {
  saved: boolean;
  dirty: boolean;
  saving: boolean;
  // Another action is running whose result a save or Send would race: a send,
  // a Revise, a Regenerate, a rename or a delete.
  held: boolean;
  // Saving waits on the deal: until it loads, its contract isn't known.
  saveUnavailable: boolean;
  // contractsapi still saves this draft; it won't once the deal is closed, or
  // once a Send has started.
  savable: boolean;
  // contractsapi offers Send on this draft.
  sendable: boolean;
  // A Send has started and not ended; Send is offered again to finish it.
  sendStarted: boolean;
  // Recipients who sign; a proposal goes nowhere without at least one.
  signerCount: number;
  // The places this template version has for a signature; contractsapi
  // refuses to save or send more signers than that.
  signaturePlaces: number;
  // Fields the rep types still empty, which contractsapi refuses to send.
  blankCount: number;
  saveError?: string;
  sending: boolean;
  // Send waits on the account's e-sign connection.
  connection: SendConnection;
  onCancel: () => void;
  onSave: () => void;
  onSend: () => void;
}

const SaveBar: React.FC<SaveBarProps> = ({
  saved,
  dirty,
  saving,
  held,
  saveUnavailable,
  savable,
  sendable,
  sendStarted,
  signerCount,
  signaturePlaces,
  blankCount,
  saveError,
  sending,
  connection,
  onCancel,
  onSave,
  onSend
}) => {
  const hasSigner = signerCount > 0;
  // A Send that started goes on as it went out, so blanks and surplus signers
  // no longer hold it.
  const blanksHold = blankCount > 0 && !sendStarted;
  const signersHold = signerCount > signaturePlaces && !sendStarted;
  const { t } = useTranslation();
  const tooManySigners = t("contracts.proposals.editor.tooManySigners", {
    count: signaturePlaces
  });
  const renderNotice = () => {
    if (saveError) {
      return (
        <Text
          variant="smBody"
          className="text-negative truncate"
          role="alert"
          data-test="proposal-save-error"
        >
          {saveError}
        </Text>
      );
    }
    if (sendStarted) {
      return (
        <Text
          variant="smBodySecondary"
          className="truncate"
          data-test="proposal-send-in-progress"
        >
          {t("contracts.proposals.editor.sendInProgress")}
        </Text>
      );
    }
    if (!savable) {
      return (
        <Text
          variant="smBodySecondary"
          className="truncate"
          data-test="proposal-save-closed"
        >
          {t("contracts.proposals.editor.saveDealClosed")}
        </Text>
      );
    }
    if (connection.missing === "admin") {
      return (
        <Text
          variant="smBodySecondary"
          className="truncate"
          data-test="proposal-send-connect"
        >
          {t("contracts.proposals.editor.sendNeedsConnection.admin")}{" "}
          <Link to={PANDADOC_SETTINGS_PATH} className="text-cta">
            {t("contracts.proposals.editor.sendNeedsConnection.connect")}
          </Link>
        </Text>
      );
    }
    if (connection.missing === "rep") {
      return (
        <Text
          variant="smBodySecondary"
          className="truncate"
          data-test="proposal-send-ask-admin"
        >
          {t("contracts.proposals.editor.sendNeedsConnection.rep")}
        </Text>
      );
    }
    return null;
  };
  // Why Send is off, in the order the notice beside it checks. A send under
  // way already says so on the button.
  const sendDisabledReason = (): string | undefined => {
    if (sending) return undefined;
    if (saving || held)
      return t("contracts.proposals.editor.sendDisabled.busy");
    if (saveUnavailable) {
      return t("contracts.proposals.editor.sendDisabled.loading");
    }
    if (!sendStarted && !savable) {
      return t("contracts.proposals.editor.sendDisabled.dealClosed");
    }
    if (connection.held) {
      return connection.missing
        ? t(
            `contracts.proposals.editor.sendNeedsConnection.${connection.missing}`
          )
        : t("contracts.proposals.editor.sendDisabled.loading");
    }
    if (!sendable)
      return t("contracts.proposals.editor.sendDisabled.unavailable");
    if (!hasSigner)
      return t("contracts.proposals.editor.sendDisabled.noSigner");
    if (signersHold) return tooManySigners;
    if (blanksHold) {
      return t("contracts.proposals.editor.sendDisabled.blanks", {
        count: blankCount
      });
    }
    return undefined;
  };
  return (
    <div
      className="absolute bottom-2 inset-x-2 z-10 flex items-center justify-between gap-2 p-2 rounded-xl border-[0.5px] border-border-border2 bg-surface-floating-panel shadow-popUp"
      data-test="proposal-save-bar"
    >
      <Button variant="secondaryNoBg" onClick={onCancel}>
        {t("cancel")}
      </Button>
      <div className="flex items-center gap-2 min-w-0">
        {renderNotice()}
        <Button
          variant="secondary"
          onClick={onSave}
          disabled={
            saving ||
            held ||
            saveUnavailable ||
            !savable ||
            (saved && !dirty) ||
            signersHold
          }
          tooltipText={signersHold ? tooManySigners : undefined}
          data-test="proposal-save"
        >
          {saved
            ? t("contracts.proposals.editor.saveChanges")
            : t("contracts.proposals.editor.saveDraft")}
        </Button>
        <Button
          variant="primary"
          onClick={onSend}
          disabled={
            !hasSigner ||
            signersHold ||
            blanksHold ||
            saving ||
            sending ||
            held ||
            saveUnavailable ||
            !sendable ||
            // A Send that started goes on as it went out, unsaved or not.
            (!sendStarted && !savable) ||
            connection.held
          }
          tooltipText={sendDisabledReason()}
          aria-busy={sending}
          data-test="proposal-send"
        >
          {sending ? (
            <Spinner size="sm" />
          ) : (
            <IconContainer as="span" size="sm" className="shrink-0">
              <Send />
            </IconContainer>
          )}
          {sending
            ? t("contracts.proposals.editor.sending")
            : t("contracts.proposals.editor.sendForSignature")}
        </Button>
      </div>
    </div>
  );
};

const SKELETON = "rounded-lg bg-surface-hover-soft animate-pulse";
const noop = () => {};

const LoadingSection: React.FC<{
  section: ProposalSection;
  first: boolean;
}> = ({ section, first }) => (
  <div
    className={
      first
        ? "flex flex-col gap-4"
        : "flex flex-col gap-4 pt-8 mt-8 border-t border-border-border1"
    }
    data-test={`proposal-section-${section.id}-loading`}
  >
    <Text variant="smSubtitle">{section.title}</Text>
    <div className="flex flex-col gap-4 px-2">
      <div className={`h-16 ${SKELETON}`} />
      <div className={`h-16 ${SKELETON}`} />
      <div className={`h-16 ${SKELETON}`} />
      <div className={`h-36 ${SKELETON}`} />
      <div className={`h-7 w-28 ${SKELETON}`} />
    </div>
  </div>
);

// The template's sections are known before generation finishes, so the rail
// and titles are real and only each section's fields are skeletons.
const LoadingState: React.FC<{ sections: ProposalSection[] }> = ({
  sections
}) => {
  const { t } = useTranslation();
  const railSections = useRailSections(sections);
  return (
    <>
      <div className="w-[200px] shrink-0">
        <SectionRail
          sections={railSections}
          outstandingBySection={{}}
          activeSectionId={sections[0]?.id}
          onSelect={noop}
        />
      </div>
      <div
        className={`w-[520px] shrink-0 overflow-hidden p-6 ${CARD}`}
        data-test="proposal-field-panel-loading"
      >
        {sections.map((section, index) => (
          <LoadingSection
            key={section.id}
            section={section}
            first={index === 0}
          />
        ))}
      </div>
      <div className={`relative flex-1 min-w-0 p-2 ${DOCUMENT_PANE}`}>
        <div className="absolute top-2 left-2 flex">
          <ProposalStatusBadge proposal={{ status: ProposalStatus.DRAFT }} />
        </div>
        <div className="flex flex-col h-full items-center justify-center gap-3">
          <Spinner size="md" />
          <Text variant="bodySecondary">
            {t("contracts.proposals.editor.generating")}
          </Text>
        </div>
      </div>
    </>
  );
};

// A slot differs when what the rep sees in it differs, however it got there.
const differs = (
  document: ProposalDocument,
  values: SlotValues,
  baseline: SlotValues
) =>
  document.slots.some(
    (slot) => slotValue(slot, values) !== slotValue(slot, baseline)
  );

export interface ProposalEditorActions {
  // Rejects with the server's refusal when the save is refused.
  onSave: (save: SaveVariables) => Promise<StoredDraft>;
  onRename: (proposal: Proposal, name: string) => Promise<unknown>;
  // Rebuilds the document from the latest deal data; writes nothing.
  onRegenerate: (
    proposal: Proposal,
    saved: boolean
  ) => Promise<ProposalDocument>;
  onDelete: (proposal: Proposal) => Promise<unknown>;
  // Sends the stored draft at `basedOn` for signature. Rejects with the
  // server's refusal, or with SendStillRunningError when it is still going.
  onSend: (proposal: Proposal, basedOn: ProposalVersion) => Promise<unknown>;
}

interface EditorBodyProps
  extends ProposalEditorActions,
    Pick<ReadOnlyProposalActions, "onRevise" | "onWithdraw"> {
  dealId: string;
  // Where leaving returns to, when not the deal's Terms & Services.
  returnTo?: string;
  dealName?: string;
  initialDocument: ProposalDocument;
  saveUnavailable: boolean;
  // Set when the rep already confirmed Regenerate from the Proposals table.
  regenerateOnOpen: boolean;
  // Why Regenerate is unavailable; shown on the disabled option.
  regenerateBlockedReason?: string;
  // Holds Regenerate until the contract's Terms & Services are known.
  regenerateHeld?: boolean;
  // Chosen on Generate, before the proposal existed.
  initialRecipients?: ProposalRecipients;
}

const EditorBody: React.FC<EditorBodyProps> = ({
  dealId,
  returnTo,
  dealName,
  initialDocument,
  saveUnavailable,
  regenerateOnOpen,
  regenerateBlockedReason,
  regenerateHeld = false,
  initialRecipients,
  onSave,
  onRename,
  onRegenerate,
  onDelete,
  onSend,
  onRevise,
  onWithdraw
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const api = useProposalsApi();
  // Replaced by Regenerate; the rep's values and the saved baseline stay here.
  const [document, setDocument] = useState(initialDocument);
  const [renaming, setRenaming] = useState(false);
  // True from a Regenerate until the save that makes it a version.
  const [regenerated, setRegenerated] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirming, setConfirming] = useState<
    "regenerate" | "delete" | "revise" | "withdraw"
  >();
  const [revising, setRevising] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [values, setValues] = useState<SlotValues>(document.values);
  // Moved to what's on screen by the save Send makes first.
  const [baseline, setBaseline] = useState<SlotValues>(document.values);
  // Kept across a Regenerate: it rebuilds the document, not who receives it.
  const [recipients, setRecipients] = useState(
    initialRecipients ?? document.recipients
  );
  const [recipientsBaseline, setRecipientsBaseline] = useState(
    document.recipients
  );
  const [editingRecipients, setEditingRecipients] = useState(false);
  const [saved, setSaved] = useState(document.saved);
  // A Send has started: the draft stays as it went out until the Send ends.
  // The server's proposal, refetched after every Send, says whether it's
  // locked. A refusal saying so locks it until the next copy arrives, which
  // then decides, so a Send that later unlocks the draft unlocks the editor.
  const latestProposal = useRef(initialDocument.proposal);
  useLayoutEffect(() => {
    latestProposal.current = initialDocument.proposal;
  }, [initialDocument.proposal]);
  const [lockedOn, setLockedOn] = useState<Proposal>();
  const lockUntilRefetched = () => setLockedOn(latestProposal.current);
  const sendStarted =
    isSendInProgress(initialDocument.proposal) ||
    lockedOn === initialDocument.proposal;
  // A proposal not saved yet has no record to rename; its first Save stores
  // the name with it.
  const {
    name,
    rename,
    pending: renamePending
  } = useProposalRename(document.proposal, onRename, saved);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const [accessRequired, setAccessRequired] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState(
    document.sections[0]?.id
  );
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  const proposal = useMemo(
    () => ({ ...document.proposal, name }),
    [document.proposal, name]
  );
  // Before the first save a new name lives only here, so it counts as an
  // unsaved change; after it, a rename is stored as soon as it's confirmed.
  const nameUnsaved = !saved && name !== initialDocument.proposal.name;
  const recipientsUnsaved = !sameRecipients(recipients, recipientsBaseline);
  const dirty = useMemo(
    () =>
      regenerated ||
      nameUnsaved ||
      recipientsUnsaved ||
      differs(document, values, baseline),
    [regenerated, nameUnsaved, recipientsUnsaved, document, values, baseline]
  );
  const railSections = useRailSections(document.sections);
  const { requestLeave, leavePending, discard, stay } = useLeaveGuard(dirty);
  const connection = useSendConnection();

  // A flowing template's document is drawn here from the rep's values as they
  // type; the server composes it only to download or send.
  const included = useMemo(
    () => document.resolved && includedOf(document.resolved),
    [document.resolved]
  );
  const liveDocument = useMemo(() => {
    if (!document.resolved) return undefined;
    const slots = new Map(document.slots.map((slot) => [slot.id, slot]));
    // Blank exactly where the field panel marks it blank, spaces included.
    return withValues(document.resolved, (slotId) => {
      // The proposal's name follows a rename, as the composer's does, unless typed over.
      if (slotId === PROPOSAL_NAME_SLOT && values[slotId] === undefined) {
        return name;
      }
      const slot = slots.get(slotId);
      if (!slot) return undefined;
      return isBlank(slot, values) ? null : slotValue(slot, values);
    });
  }, [document.resolved, document.slots, values, name]);
  const readingOrder = useMemo(
    () => document.resolved && slotsInReadingOrder(document.resolved),
    [document.resolved]
  );
  // What contractsapi checks before a flowing template's Send; it doesn't check
  // a fixed-box one. A field only in an optional block left out holds nothing.
  const shownSlots = useMemo(
    () => document.resolved && slotsShown(document.resolved),
    [document.resolved]
  );
  const templateFiles = useTemplateFiles(document.resolved);
  // The pages are then drawn without the template's background or fonts, so they
  // can differ from the PDF; Download still gives the exact document.
  const warnTemplateAssets = useCallback(
    () => toast.error(t("contracts.proposals.editor.templateAssetsError")),
    [t]
  );
  useEffect(() => {
    if (templateFiles.isError) warnTemplateAssets();
  }, [templateFiles.isError, warnTemplateAssets]);
  const fieldPanelRef = useRef<HTMLDivElement>(null);
  // A field picked on the page focuses its control, never the flag marker beside it.
  const onPickField = useCallback((slotId: string) => {
    fieldPanelRef.current
      ?.querySelector(`[data-test="proposal-slot-${CSS.escape(slotId)}"]`)
      ?.querySelector<HTMLElement>(
        "input:not([type='file']), textarea, [contenteditable='true'], [role='textbox'][tabindex='0']"
      )
      ?.focus();
  }, []);

  // Typed values show at once through the overlay; the document itself is
  // recomposed once the rep pauses, or at once when they leave the field.
  const [recomposeValues, setRecomposeValues] = useState<SlotValues>();
  const preview = useProposalPreview({
    api,
    document,
    values: recomposeValues ?? {},
    enabled: !!recomposeValues
  });
  const composed = (recomposeValues && preview.data) || {
    documentUrl: document.documentUrl,
    composedFlags: document.composedFlags,
    composedValues: document.composedValues
  };
  const refreshing = preview.isFetching || regenerating;

  // The typed values stay drawn over the last good document.
  useEffect(() => {
    if (preview.errorUpdatedAt) {
      toast.error(t("contracts.proposals.editor.previewError"));
    }
  }, [preview.errorUpdatedAt, t]);

  // Skips values the document was last recomposed with, so the same values are
  // never composed twice.
  const recompose = useCallback(
    (next: SlotValues) =>
      setRecomposeValues((current) =>
        differs(document, next, current ?? document.composedValues)
          ? next
          : current
      ),
    [document]
  );

  // Scheduled by each edit with the values it produced, so only typing starts
  // one; a document reload after a save can't replay an older snapshot.
  const recomposeAfterPause = useDebouncedCallback(
    recompose,
    RECOMPOSE_PAUSE_MS
  );

  const renderOverlay: PageOverlay = (pageNumber, scale, sampleColor) => (
    <SlotOverlay
      slots={document.slots}
      values={values}
      composedValues={composed.composedValues}
      pageNumber={pageNumber}
      scale={scale}
      sampleColor={sampleColor}
      highlightedSlotId={focusedSlotId}
    />
  );

  const outstandingBySection = useMemo(() => {
    const counts: Record<string, number> = {};
    document.slots.forEach((slot) => {
      if (needsInput(slot, values)) {
        counts[slot.sectionId] = (counts[slot.sectionId] ?? 0) + 1;
      }
    });
    return counts;
  }, [document.slots, values]);

  const onChange = useCallback(
    (slotId: string, value: string) => {
      const next = { ...values, [slotId]: value };
      setValues(next);
      if (!document.resolved) recomposeAfterPause(next);
    },
    [values, recomposeAfterPause, document.resolved]
  );

  // A new object each time, so returning to the same field scrolls again.
  const [located, setLocated] = useState<{ slotId: string }>();
  // The field in focus, tinted in the document while it keeps focus.
  const [focusedSlotId, setFocusedSlotId] = useState<string>();
  const onLocate = useCallback((slotId: string) => {
    setLocated({ slotId });
    setFocusedSlotId(slotId);
  }, []);
  const onLeave = useCallback(
    (slotId: string) =>
      setFocusedSlotId((current) => (current === slotId ? undefined : current)),
    []
  );

  const onSelectSection = (sectionId: string) => {
    setActiveSectionId(sectionId);
    sectionRefs.current[sectionId]?.scrollIntoView({ block: "start" });
  };

  const leave = () =>
    requestLeave(() => navigate(returnTo ?? dealPath(dealId)));

  // A save goes straight back to the deal.
  // Leaving by navigate isn't caught by the leave guard: nothing is unsaved.
  const save = async () => {
    setSaving(true);
    setSaveError(undefined);
    try {
      await onSave({
        document,
        proposal,
        values,
        regenerated,
        recipients,
        included
      });
      navigate(returnTo ?? dealPath(dealId));
      toast.success(t("contracts.proposals.editor.savedAsDraft"));
    } catch (error) {
      // The rep's values stay as typed; only the reason is added.
      setSaveError(t(saveErrorMessageKey(error)));
      if (isRefusedWhileSending(error)) lockUntilRefetched();
      setSaving(false);
    }
  };

  // What's on screen is now the stored draft; a refused Send keeps it so.
  const markStored = (stored: StoredDraft) => {
    setDocument((current) => ({
      ...current,
      proposal: { ...stored.proposal, name },
      saved: true,
      version: stored.version
    }));
    setBaseline(values);
    setRecipientsBaseline(recipients);
    setRegenerated(false);
    setSaved(true);
  };

  // Saves unsaved edits first, so what goes out is what the rep sees. A draft
  // whose Send started is locked, so it goes on as it is.
  const storedVersion = async (): Promise<ProposalVersion> => {
    if (document.version && (sendStarted || (saved && !dirty))) {
      return document.version;
    }
    const stored = await onSave({
      document,
      proposal,
      values,
      regenerated,
      recipients,
      included
    });
    markStored(stored);
    return stored.version;
  };

  // On success the proposal opens as sent: the hook writes it to the document.
  const send = async () => {
    setSending(true);
    setSaveError(undefined);
    let version: ProposalVersion;
    try {
      version = await storedVersion();
    } catch (error) {
      // The save before it was refused: its reason, not a Send's.
      setSaveError(t(saveErrorMessageKey(error)));
      if (isRefusedWhileSending(error)) lockUntilRefetched();
      setSending(false);
      return;
    }
    try {
      await onSend(proposal, version);
    } catch (error) {
      // The rep's values stay as typed; only the reason is added.
      if (isSenderNotMatched(error)) setAccessRequired(true);
      else
        setSaveError(
          blankFieldsMessage(error) ?? t(sendErrorMessageKey(error))
        );
      if (isSendStillRunning(error)) lockUntilRefetched();
      setSending(false);
    }
  };

  // A regenerated document isn't cached, so the editor releases its PDF itself
  // once a newer one replaces it or the editor closes; one that arrives after
  // the editor closed is released at once.
  const regeneratedUrl = useRef<string>();
  const closed = useRef(false);
  useEffect(() => {
    closed.current = false;
    return () => {
      closed.current = true;
      releaseDocumentUrl(regeneratedUrl.current);
    };
  }, []);

  // The rebuilt document replaces the rep's edits and stays unsaved.
  const regenerate = async () => {
    setConfirming(undefined);
    setRegenerating(true);
    try {
      const next = await onRegenerate(proposal, saved);
      if (closed.current) {
        releaseDocumentUrl(next.documentUrl);
        return;
      }
      recomposeAfterPause.cancel();
      setRecomposeValues(undefined);
      // Still based on the stored version it was opened at.
      setDocument({ ...next, version: document.version });
      releaseDocumentUrl(regeneratedUrl.current);
      regeneratedUrl.current = next.documentUrl;
      setValues(next.values);
      setRegenerated(true);
      setSaveError(undefined);
    } catch (error) {
      toast.error(
        t(
          generateErrorMessageKey(
            error,
            "contracts.proposals.editor.regenerateError"
          )
        )
      );
    } finally {
      setRegenerating(false);
    }
  };

  // Once, on open; the dialog was already confirmed on the Proposals table.
  const regeneratedOnOpen = useRef(false);
  useEffect(() => {
    if (!regenerateOnOpen || regeneratedOnOpen.current) return;
    regeneratedOnOpen.current = true;
    // Confirmed on a row the table may have read before a Send started
    if (!isRegenerable(proposal) || sendStarted) return;
    regenerate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regenerateOnOpen]);

  // A proposal never saved has nothing stored, so deleting it just leaves.
  // Leaving by navigate isn't caught by the leave guard, which is intended:
  // the rep has just confirmed discarding it.
  const remove = async () => {
    setConfirming(undefined);
    setDeleting(true);
    try {
      if (saved) await onDelete(proposal);
      navigate(returnTo ?? dealPath(dealId));
    } catch (error) {
      setDeleting(false);
      // Refused because a Send started: the lock's notice says why.
      if (isRefusedWhileSending(error)) lockUntilRefetched();
      else toast.error(t("contracts.proposals.deleteError"));
    }
  };

  // The page reopens the editor on the new draft once it lands; a refusal
  // leaves the proposal as it was, so the rep can try again.
  const revise = async () => {
    setConfirming(undefined);
    setRevising(true);
    try {
      await onRevise(proposal);
    } catch (error) {
      setRevising(false);
      toast.error(t(reviseErrorMessageKey(error)));
    }
  };

  // A Send that started is withdrawn as it went out; the page then reads the
  // proposal again and opens it, Voided, for reading.
  const withdraw = async () => {
    setConfirming(undefined);
    setWithdrawing(true);
    try {
      await onWithdraw(proposal);
      toast.success(t("contracts.proposals.withdrawn"));
    } catch (error) {
      toast.error(t(withdrawErrorMessageKey(error)));
    } finally {
      setWithdrawing(false);
    }
  };

  // Held while a save, Regenerate, rename, delete, Revise, Withdraw or download
  // runs: whichever lands last would overwrite what the others changed, and a
  // Regenerate would release the PDF a download is about to fetch.
  const busy =
    saving ||
    sending ||
    regenerating ||
    renamePending ||
    deleting ||
    revising ||
    withdrawing ||
    downloading;
  // A Send that never finished can't be edited; Revise is the way out of it.
  const revisable = isRevisable(initialDocument.proposal);
  const menuOptions: DetailedViewOptions = {
    rename: renameOption(() => setRenaming(true), busy),
    ...(revisable
      ? { revise: reviseOption(() => setConfirming("revise"), busy) }
      : {}),
    // Also on a closed deal, where a Send that started has no Revise.
    ...(isWithdrawable(initialDocument.proposal)
      ? { withdraw: withdrawOption(() => setConfirming("withdraw"), busy) }
      : {}),
    // A draft whose Send started stays as it went out; one on a closed deal can't be saved.
    ...(isRegenerable(proposal) && !sendStarted
      ? {
          regenerate: regenerateOption(
            () => setConfirming("regenerate"),
            busy || regenerateHeld,
            regenerateBlockedReason
          )
        }
      : {}),
    ...(isDeletable(proposal) && !sendStarted
      ? {
          delete: {
            action: () => setConfirming("delete"),
            disabled: busy,
            variant: "destructive" as const
          }
        }
      : {})
  };

  // The file holds what the rep sees: an edit the document doesn't have yet is
  // composed first.
  const documentUrlToDownload = async () => {
    if (!differs(document, values, composed.composedValues)) {
      return composed.documentUrl;
    }
    setRecomposeValues(values);
    const recomposed = await queryClient.fetchQuery(
      proposalPreviewQuery({ api, document, values })
    );
    return recomposed.documentUrl;
  };

  const download = async () => {
    setDownloading(true);
    try {
      await downloadFile(
        await documentUrlToDownload(),
        proposalFileName(
          name,
          t(`contracts.proposals.status.${document.proposal.status}`)
        )
      );
    } catch {
      toast.error(t("contracts.proposals.editor.downloadError"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <EditorHeader
        dealId={dealId}
        dealName={dealName}
        title={
          <ProposalTitle
            name={name}
            editing={renaming}
            onRename={rename}
            onDone={() => setRenaming(false)}
          />
        }
        actions={
          <>
            <DownloadButton
              onClick={download}
              disabled={downloading || regenerating}
            />
            <ProposalActionsMenu options={menuOptions} />
          </>
        }
      />
      <div className={PANE_ROW}>
        <div className="w-[200px] shrink-0">
          <SectionRail
            sections={railSections}
            outstandingBySection={outstandingBySection}
            activeSectionId={activeSectionId}
            onSelect={onSelectSection}
          />
        </div>
        <div
          // The scrollbar runs on a clear lane inset from the rounded corners.
          className={`w-[520px] shrink-0 overflow-y-auto p-6 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-track]:my-1 ${CARD}`}
          ref={fieldPanelRef}
          // A flowing document isn't composed while the rep types, so leaving a field
          // composes it to refresh what it flags (cut text, characters the PDF can't draw).
          onBlur={() =>
            document.resolved ? recompose(values) : recomposeAfterPause.flush()
          }
          data-test="proposal-field-panel"
        >
          {document.sections.map((section, index) => {
            const slots = inDocumentOrder(
              document.slots.filter((slot) => slot.sectionId === section.id),
              readingOrder
            );
            const outstanding = outstandingBySection[section.id] ?? 0;
            return (
              <section
                key={section.id}
                ref={(element) => {
                  sectionRefs.current[section.id] = element;
                }}
                className={
                  index > 0
                    ? "flex flex-col gap-4 pt-8 mt-8 border-t border-border-border1"
                    : "flex flex-col gap-4"
                }
                aria-labelledby={`proposal-section-${section.id}`}
                data-test={`proposal-section-${section.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <Text
                    variant="smSubtitle"
                    id={`proposal-section-${section.id}`}
                  >
                    {section.title}
                  </Text>
                  {outstanding > 0 ? (
                    <Text
                      variant="smBodySecondary"
                      data-test={`proposal-section-${section.id}-outstanding`}
                    >
                      {t("contracts.proposals.editor.sectionOutstanding", {
                        outstanding,
                        total: slots.length
                      })}
                    </Text>
                  ) : null}
                </div>
                {slots.length === 0 ? (
                  <Text
                    variant="bodyTertiary"
                    className="px-2"
                    data-test={`proposal-section-${section.id}-template`}
                  >
                    {t("contracts.proposals.editor.templateContent")}
                  </Text>
                ) : (
                  <div className="flex flex-col gap-6 px-2">
                    {slots.map((slot) => (
                      <SlotField
                        key={slot.id}
                        slot={slot}
                        value={slotValue(slot, values)}
                        blank={isBlank(slot, values)}
                        flag={slotFlag(slot.id, composed.composedFlags)}
                        onChange={onChange}
                        locked={saving || sending || sendStarted}
                        onLocate={onLocate}
                        onLeave={onLeave}
                      />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
          <section
            ref={(element) => {
              sectionRefs.current[RECIPIENTS_SECTION_ID] = element;
            }}
            className="pt-8 mt-8 border-t border-border-border1"
            aria-labelledby="proposal-recipients-title"
            data-test="proposal-section-recipients"
          >
            <RecipientsCard
              recipients={recipients}
              onEdit={() => setEditingRecipients(true)}
              disabled={
                saving ||
                sending ||
                sendStarted ||
                regenerating ||
                renamePending ||
                deleting ||
                revising ||
                withdrawing
              }
            />
          </section>
        </div>
        <DocumentPane
          proposal={document.proposal}
          url={composed.documentUrl}
          refreshing={refreshing}
          onClose={leave}
          renderOverlay={renderOverlay}
          renderPages={
            liveDocument
              ? (width, onPageCount) =>
                  templateFiles.isPending ? (
                    <div className="flex justify-center py-10">
                      <Spinner size="md" />
                    </div>
                  ) : (
                    <HighlightedSlotContext.Provider value={focusedSlotId}>
                      <ProposalPages
                        resolvedDocument={liveDocument}
                        backgroundUrl={templateFiles.data?.backgroundUrl}
                        fontUrls={templateFiles.data?.fontUrls}
                        scale={width / liveDocument.page.size[0]}
                        onField={onPickField}
                        onPageCount={onPageCount}
                        onAssetsFailed={warnTemplateAssets}
                      />
                    </HighlightedSlotContext.Provider>
                  )
              : undefined
          }
          locate={located}
          footer={
            <SaveBar
              saved={saved}
              dirty={dirty}
              saving={saving}
              held={
                sending ||
                revising ||
                withdrawing ||
                regenerating ||
                renamePending ||
                deleting
              }
              saveUnavailable={saveUnavailable}
              savable={isSavable(proposal) && !sendStarted}
              sendable={isSendable(proposal)}
              sendStarted={sendStarted}
              signerCount={signerCount(recipients.list)}
              signaturePlaces={document.signaturePlaces}
              blankCount={
                shownSlots
                  ? document.slots.filter(
                      (slot) =>
                        shownSlots.has(slot.id) && holdsSend(slot, values)
                    ).length
                  : 0
              }
              saveError={saveError}
              sending={sending}
              connection={connection}
              onCancel={leave}
              onSave={save}
              onSend={send}
            />
          }
        />
      </div>
      <RecipientsModal
        open={editingRecipients}
        dealId={dealId}
        recipients={recipients}
        signaturePlaces={document.signaturePlaces}
        confirmLabel={t("contracts.proposals.recipients.save")}
        onContinue={(next) => {
          setEditingRecipients(false);
          setRecipients(next);
          if (!sameRecipients(next, recipients)) {
            toast.success(t("contracts.proposals.recipients.updated"));
          }
        }}
        onCancel={() => setEditingRecipients(false)}
      />
      <SendAccessRequiredDialog
        isOpen={accessRequired}
        onClose={() => setAccessRequired(false)}
      />
      <RegenerateProposalDialog
        proposal={confirming === "regenerate" ? proposal : undefined}
        onConfirm={regenerate}
        onCancel={() => setConfirming(undefined)}
      />
      <DeleteProposalDialog
        proposal={confirming === "delete" ? proposal : undefined}
        onConfirm={remove}
        onCancel={() => setConfirming(undefined)}
      />
      <ReviseProposalDialog
        proposal={confirming === "revise" ? proposal : undefined}
        recipients={initialDocument.recipients.list}
        withdraws={withdrawsOnRevise(initialDocument.proposal)}
        onConfirm={revise}
        onCancel={() => setConfirming(undefined)}
      />
      <WithdrawProposalDialog
        proposal={confirming === "withdraw" ? proposal : undefined}
        recipients={initialDocument.recipients.list}
        onConfirm={withdraw}
        onCancel={() => setConfirming(undefined)}
      />
      <UnsavedChangesWarningDialog
        isOpen={leavePending}
        title={t("contracts.proposals.editor.leave.title")}
        message={t("contracts.proposals.editor.leave.message")}
        discardButtonText={t("contracts.proposals.editor.leave.discard")}
        cancelButtonText={t("contracts.proposals.editor.leave.continue")}
        sentenceCaseTitle
        onDiscard={discard}
        onCancel={stay}
      />
    </>
  );
};

interface Props extends ProposalEditorActions, ReadOnlyProposalActions {
  dealId: string;
  returnTo?: string;
  dealName?: string;
  // The active template's sections, shown while the document generates.
  sections: ProposalSection[];
  // Absent while the proposal is still being generated.
  document?: ProposalDocument;
  // The proposal couldn't be opened; the rep can ask again.
  openError?: boolean;
  // This session deleted the proposal, so there is nothing left to open.
  deleted?: boolean;
  onRetryOpen?: () => void;
  // True until the deal loads: which contract a save goes to isn't known yet.
  saveUnavailable?: boolean;
  regenerateOnOpen?: boolean;
  // Why a proposal can't be rebuilt from the contract yet.
  regenerateBlockedReason?: string;
  // True until the deal and its contract's Terms & Services are known.
  regenerateHeld?: boolean;
  initialRecipients?: ProposalRecipients;
  // Counts the Revises that landed here: each reopens the editor on the new
  // draft, with nothing kept from the version it replaced.
  revision?: number;
}

export const ProposalEditor: React.FC<Props> = ({
  dealId,
  returnTo,
  dealName,
  sections,
  document,
  openError = false,
  deleted = false,
  onRetryOpen,
  saveUnavailable = false,
  regenerateOnOpen = false,
  regenerateBlockedReason,
  regenerateHeld = false,
  initialRecipients,
  revision = 0,
  onRevise,
  ...actions
}) => (
  <div
    className={`${PAGE_HEIGHT_CLASS} w-full max-w-[1800px] mx-auto`}
    data-test="proposal-editor"
  >
    {document && document.proposal.status !== ProposalStatus.DRAFT ? (
      <ReadOnlyProposalView
        key={document.proposal.id}
        dealId={dealId}
        returnTo={returnTo}
        dealName={dealName}
        document={document}
        regenerateBlockedReason={regenerateBlockedReason}
        regenerateHeld={regenerateHeld}
        onRename={actions.onRename}
        onRevise={onRevise}
        onWithdraw={actions.onWithdraw}
      />
    ) : document ? (
      <EditorBody
        key={`${document.proposal.id}:${revision}`}
        dealId={dealId}
        returnTo={returnTo}
        dealName={dealName}
        initialDocument={document}
        saveUnavailable={saveUnavailable}
        regenerateOnOpen={regenerateOnOpen}
        regenerateBlockedReason={regenerateBlockedReason}
        regenerateHeld={regenerateHeld}
        initialRecipients={initialRecipients}
        onRevise={onRevise}
        {...actions}
      />
    ) : deleted ? (
      <>
        <EditorHeader dealId={dealId} dealName={dealName} />
        <DeletedNotice />
      </>
    ) : openError ? (
      <>
        <EditorHeader dealId={dealId} dealName={dealName} />
        <OpenError onRetry={onRetryOpen} />
      </>
    ) : (
      <>
        <EditorHeader dealId={dealId} dealName={dealName} />
        <div className={PANE_ROW}>
          <LoadingState sections={sections} />
        </div>
      </>
    )}
  </div>
);
